// External Imports
import { Fragment, useContext, useState } from "react";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import DatePair from "../UI/DatePair";
import LoadingSpinner from "../UI/LoadingSpinner";
import { requestAdminAPI } from "../../api/posh-api";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { getISODate } from "../../util/format-date";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";
import { UtilityContext } from "../../context/util-context";
import SelectPair from "../UI/SelectPair";

// Internal Imports

/** ========================================================================
 * * AddPartner Component
 * This component renders a form to add a partner.
 * ========================================================================== */

export default function AddPartner() {
  const { navigate, setConfirm } = useContext(UtilityContext);
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [app, setApp] = useState();
  const [type, setType] = useState("individual");

  /* ====================== FETCH APPLICATION ====================== */
  const fetchApplication = async () => {
    try {
      setLoading(true);
      const email = document.getElementById("email").value;
      const response = await requestAdminAPI("fetch-fap-app", { email });
      if (response.businessName) setType("business");
      setApp(response);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  /* ====================== ADD PARTNER ====================== */
  const addPartner = async () => {
    try {
      setLoading(true);
      const payload = {
        ...app,
        type: type,
        email: document.getElementById("email").value,
        name: document.getElementById("name").value,
        phone: document.getElementById("phone").value,
        dob: document.getElementById("dob")?.value,
        address: document.getElementById("address")?.value,
        driversLicenseNumber: document.getElementById("licenseNumber")?.value,
        businessName: document.getElementById("businessName")?.value,
        businessAddress: document.getElementById("businessAddress")?.value,
        ein: document.getElementById("ein")?.value,
      };
      const files = {
        front: document.getElementById("licenseFront").files[0],
        back: document.getElementById("licenseBack").files[0],
      };
      if (files.front)
        payload.driversLicenseFront = await uploadFile(
          `partners/${payload.email}/driversLicenseFront`,
          files.front,
          () => {}
        );
      if (files.back)
        payload.driversLicenseBack = await uploadFile(
          `partners/${payload.email}/driversLicenseFront`,
          files.back,
          () => {}
        );

      // Send Request
      const partnerId = await requestAdminAPI("add-partner", payload);
      navigate(`/partners/info?id=${partnerId}`);
      setLoading(false);
    } catch (err) {
      console.error("Error adding partner:", err);
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <Heading
        title="Add Partner"
        isBackArrow={true}
        text="If found, the FAP application will be used to automatically fill the form."
      />
      <div className="info">
        <DetailPair
          label="Email"
          edit={true}
          inputOptions={{
            id: "email",
            type: "email",
            onChange: () => app && setApp(),
          }}
        />
        {app?.id ? (
          <div className="flex self-end mb-3 space-x-2 lg:col-span-3">
            <MdCheckCircle className="text-xl text-green-600" />
            <p className="text-green-500">FAP Application Found</p>
          </div>
        ) : app ? (
          <div className="flex self-end mb-3 space-x-2 lg:col-span-3">
            <MdCancel className="text-xl text-red-600" />
            <p className="text-red-500">FAP Application Not Found.</p>
          </div>
        ) : (
          <div className="lg:col-span-3"></div>
        )}
      </div>
      {app && (
        <div className="w-3/4 info lg:grid-cols-3">
          <SelectPair
            label="Type"
            edit={true}
            loading={loading}
            options={[
              { id: "individual", name: "Individual" },
              { id: "business", name: "business" },
            ]}
            inputOptions={{
              id: "type",
              defaultValue: type,
              onChange: (e) => {
                setType(e.target.value);
              },
              value: type,
            }}
          />
          {type === "business" && (
            <DetailPair
              label="Business Name"
              edit={true}
              loading={loading}
              inputOptions={{
                defaultValue: app?.businessName,
                id: "businessName",
                type: "text",
              }}
            />
          )}
          {type === "business" && (
            <DetailPair
              label="Business EIN"
              edit={true}
              loading={loading}
              inputOptions={{
                defaultValue: app?.ein,
                id: "ein",
                type: "text",
              }}
            />
          )}
          <DetailPair
            label={type === "business" ? "Authorized Signatory" : "Name"}
            edit={true}
            loading={loading}
            inputOptions={{
              defaultValue: app?.name,
              id: "name",
              type: "text",
            }}
          />
          <DetailPair
            label="Phone Number"
            edit={true}
            loading={loading}
            inputOptions={{
              defaultValue: app?.phone,
              id: "phone",
              type: "tel",
            }}
          />
          <DatePair
            label="Date of Birth"
            edit={true}
            date={app.dateOfBirth && getISODate(app.dateOfBirth, "00:00:00")}
            loading={loading}
            inputOptions={{
              id: "dob",
            }}
          />
          <LinkPair
            label="Driver's License Front"
            edit={true}
            loading={loading}
            inputOptions={{
              type: "file",
              id: "licenseFront",
              accept: "image/*",
            }}
          />
          <LinkPair
            label="Driver's License Back"
            edit={true}
            loading={loading}
            inputOptions={{
              type: "file",
              id: "licenseBack",
              accept: "image/*",
            }}
          />
          <DetailPair
            label="Driver's License Number"
            edit={true}
            loading={loading}
            inputOptions={{
              type: "text",
              id: "licenseNumber",
            }}
          />
          {type === "business" && (
            <div className="lg:col-span-3">
              <DetailPair
                label="Business Address"
                edit={true}
                loading={loading}
                inputOptions={{
                  defaultValue: app?.businessAddress,
                  id: "businessAddress",
                }}
              />
            </div>
          )}
          <div className="lg:col-span-3">
            <DetailPair
              label="Home Address"
              edit={true}
              loading={loading}
              inputOptions={{
                defaultValue: app?.address,
                id: "address",
              }}
            />
          </div>
        </div>
      )}
      <div className="mt-4 info">
        {loading ? (
          <LoadingSpinner />
        ) : app ? (
          <div
            onClick={() =>
              setConfirm({
                message:
                  "This create an account for the partner with a random password. An email and sms will be sent to the partner's email address and phone number with the login credentials.",
                action: () => addPartner(),
              })
            }
            className="btn-primary"
          >
            Create Account
          </div>
        ) : (
          <div onClick={fetchApplication} className="btn-primary">
            Continue
          </div>
        )}
      </div>
    </div>
  );
}
